import { useState, useRef } from "react";
import { supabase } from "../../lib/supabase";

export interface MediaItem {
  url: string;
  type: "image" | "video";
  is_cover?: boolean;
}

interface Props {
  items: MediaItem[];
  onChange: (items: MediaItem[]) => void;
  maxItems?: number;
  folder?: string;
  label?: string;
}

const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const MediaUploader = ({ items, onChange, maxItems = 100, folder = "media", label = "Hình ảnh & Video" }: Props) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isVideoUrl = (url: string) => {
    const ext = url.split("?")[0].split(".").pop()?.toLowerCase();
    return ["mp4", "webm", "mov", "avi", "mkv"].includes(ext ?? "");
  };

  const detectType = (file: File): "image" | "video" => {
    return file.type.startsWith("video/") ? "video" : "image";
  };

  const handleFiles = async (files: FileList) => {
    const remaining = maxItems - items.length;
    const toUpload = Array.from(files).slice(0, remaining);

    const oversized = toUpload.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    if (oversized.length > 0) {
      setErrorMsg(`File quá lớn (tối đa ${MAX_FILE_SIZE_MB}MB): ${oversized.map((f) => f.name).join(", ")}`);
      setUploadStatus("error");
      return;
    }

    setUploading(true);
    setUploadStatus("uploading");
    setErrorMsg("");
    const newItems: MediaItem[] = [];
    let hasError = false;

    for (const file of toUpload) {
      const fileType = detectType(file);
      const ext = file.name.split(".").pop() ?? (fileType === "video" ? "mp4" : "jpg");
      const fileName = `${folder}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

      setUploadProgress((prev) => ({ ...prev, [file.name]: 10 }));

      const { data, error } = await supabase.storage
        .from("bizwell-images")
        .upload(fileName, file, { upsert: true, contentType: file.type });

      if (!error && data) {
        const { data: urlData } = supabase.storage.from("bizwell-images").getPublicUrl(data.path);
        if (urlData?.publicUrl) {
          newItems.push({ url: urlData.publicUrl, type: fileType });
        }
        setUploadProgress((prev) => ({ ...prev, [file.name]: 100 }));
      } else {
        hasError = true;
        setErrorMsg(error?.message ?? "Lỗi upload file");
        setUploadProgress((prev) => ({ ...prev, [file.name]: 0 }));
      }
    }

    // If no cover set yet and we have new images, auto-set first image as cover
    const currentHasCover = items.some((m) => m.is_cover);
    const allItems = [...items, ...newItems];
    if (!currentHasCover && newItems.length > 0) {
      const firstImgIdx = allItems.findIndex((m) => m.type === "image");
      if (firstImgIdx !== -1) allItems[firstImgIdx] = { ...allItems[firstImgIdx], is_cover: true };
    }

    onChange(allItems);
    setUploading(false);
    setUploadProgress({});
    setUploadStatus(hasError ? "error" : "success");
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!hasError) setTimeout(() => setUploadStatus("idle"), 3000);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  };

  const handleRemove = (index: number) => {
    const next = [...items];
    const wascover = next[index].is_cover;
    next.splice(index, 1);
    // If removed item was cover, assign cover to first image
    if (wascover) {
      const firstImgIdx = next.findIndex((m) => m.type === "image");
      if (firstImgIdx !== -1) next[firstImgIdx] = { ...next[firstImgIdx], is_cover: true };
    }
    onChange(next);
  };

  const handleSetCover = (index: number) => {
    // Only images can be cover
    if (items[index].type !== "image") return;
    const next = items.map((item, i) => ({ ...item, is_cover: i === index }));
    onChange(next);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const next = [...items];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next);
  };

  const coverIndex = items.findIndex((m) => m.is_cover);

  return (
    <div className="space-y-3">
      {label && <label className="block text-xs font-semibold text-gray-600">{label}</label>}

      {/* Status messages */}
      {uploadStatus === "success" && (
        <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-xs rounded-lg px-3 py-2">
          <i className="ri-checkbox-circle-line text-green-500" />
          <span>Tải lên thành công!</span>
        </div>
      )}
      {uploadStatus === "error" && errorMsg && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg px-3 py-2">
          <i className="ri-error-warning-line text-red-500" />
          <span>{errorMsg}</span>
          <button onClick={() => setUploadStatus("idle")} className="ml-auto cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {/* Upload zone */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => !uploading && fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-all ${uploading ? "cursor-wait border-[#004A99]/40 bg-[#004A99]/3" : "cursor-pointer hover:border-[#004A99]/50 hover:bg-[#004A99]/3 border-gray-200"}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/mp4,video/mov,video/quicktime,video/webm,video/avi,video/x-matroska"
          multiple
          className="hidden"
          onChange={(e) => e.target.files && handleFiles(e.target.files)}
        />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin" />
            <p className="text-xs text-[#004A99] font-semibold">Đang tải lên... Vui lòng chờ</p>
            {Object.entries(uploadProgress).map(([name, progress]) => (
              <div key={name} className="w-full max-w-xs">
                <div className="flex justify-between text-xs text-gray-400 mb-1">
                  <span className="truncate max-w-[200px]">{name}</span>
                  <span className="font-semibold text-[#004A99]">{progress}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#004A99] rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-center gap-3 mb-2">
              <i className="ri-image-add-line text-2xl text-gray-300" />
              <i className="ri-video-add-line text-2xl text-gray-300" />
            </div>
            <p className="text-sm font-semibold text-gray-500">Kéo thả hoặc nhấp để tải lên</p>
            <p className="text-xs text-gray-400 mt-1">Hỗ trợ ảnh (JPG, PNG, WebP) và video (MP4, MOV, WebM)</p>
            <p className="text-xs text-gray-300 mt-0.5">Tối đa {MAX_FILE_SIZE_MB}MB mỗi file</p>
          </>
        )}
      </div>

      {/* Cover hint */}
      {items.length > 0 && (
        <div className="flex items-center gap-1.5 text-[11px] text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
          <i className="ri-star-line" />
          <span>Nhấn <strong>Đặt làm ảnh đại diện</strong> để chọn ảnh hiển thị ngoài danh sách sản phẩm. Video sẽ tự động ưu tiên hiển thị ở Hero.</span>
        </div>
      )}

      {/* Media grid */}
      {items.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {items.map((item, i) => {
            const isVid = item.type === "video" || isVideoUrl(item.url);
            const isCover = item.is_cover || (coverIndex === -1 && i === 0 && !isVid);
            return (
              <div key={i} className={`relative group rounded-xl overflow-hidden bg-gray-100 aspect-video border-2 transition-all ${isCover && !isVid ? "border-amber-400" : "border-transparent"}`}>
                {isVid ? (
                  <video
                    src={item.url}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                    onMouseEnter={(e) => (e.currentTarget as HTMLVideoElement).play()}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLVideoElement).pause(); (e.currentTarget as HTMLVideoElement).currentTime = 0; }}
                  />
                ) : (
                  <img src={item.url} alt={`Media ${i + 1}`} className="w-full h-full object-cover object-top" />
                )}

                {/* Type badge */}
                <div className="absolute top-1.5 left-1.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold text-white ${isVid ? "bg-purple-600" : "bg-[#004A99]"}`}>
                    {isVid ? "VIDEO" : "ẢNH"}
                  </span>
                </div>

                {/* Cover badge */}
                {isCover && !isVid && (
                  <div className="absolute top-1.5 right-1.5">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500 text-white font-semibold flex items-center gap-0.5">
                      <i className="ri-star-fill text-[9px]" />
                      Đại diện
                    </span>
                  </div>
                )}

                {/* Controls overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-2">
                  {!isVid && !isCover && (
                    <button
                      onClick={() => handleSetCover(i)}
                      className="w-full flex items-center justify-center gap-1 py-1 bg-amber-500 hover:bg-amber-600 rounded-lg text-white text-[10px] font-semibold cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-star-line text-[10px]" />
                      Đặt làm ảnh đại diện
                    </button>
                  )}
                  <div className="flex gap-1.5">
                    {i > 0 && (
                      <button
                        onClick={() => handleMoveUp(i)}
                        className="w-7 h-7 bg-white/20 hover:bg-white/40 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                        title="Di chuyển lên"
                      >
                        <i className="ri-arrow-left-line text-white text-xs" />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemove(i)}
                      className="w-7 h-7 bg-red-500/80 hover:bg-red-600 rounded-lg flex items-center justify-center cursor-pointer transition-colors"
                      title="Xóa"
                    >
                      <i className="ri-delete-bin-line text-white text-xs" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MediaUploader;
